import { PlayCircle, Minus } from "lucide-react";
import oa from "../../img/oa_video.mp4";
import { useState, useEffect } from "react";
import preview from "../../img/OA_logo.jpg";

export default function PlatformTourWidget() {
    const [showVideo, setShowVideo] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    // Desktop widget state
    const [isMinimized, setIsMinimized] = useState(false);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(
                window.matchMedia("(max-width: 768px)").matches
            );
        };

        checkMobile();

        window.addEventListener("resize", checkMobile);

        return () => {
            window.removeEventListener("resize", checkMobile);
        };
    }, []);

    return (
        <>
            {/* =====================================================
                MOBILE
                Tiny Tour Button
            ===================================================== */}

            {isMobile && (
                <button
                    onClick={() => setShowVideo(true)}
                    className="
                        fixed
                        bottom-5
                        right-4
                        z-[9998]
                        bg-slate-900
                        text-white
                        rounded-full
                        shadow-xl
                        flex
                        items-center
                        gap-1
                        px-3
                        py-2
                        text-xs
                        font-semibold
                        border
                        border-slate-600
                    "
                >
                    <PlayCircle size={18} />
                    Tour
                </button>
            )}


            {/* =====================================================
                DESKTOP
            ===================================================== */}

            {!isMobile && (
                <>
                    {/* -------------------------------------------------
                        MINIMIZED DESKTOP BUTTON
                    ------------------------------------------------- */}

                    {isMinimized && (
                        <button
                            onClick={() => setIsMinimized(false)}
                            className="
                                fixed
                                left-6
                                top-1/2
                                -translate-y-1/2
                                z-[9998]
                                w-12
                                h-12
                                bg-slate-900
                                text-white
                                rounded-xl
                                shadow-xl
                                flex
                                items-center
                                justify-center
                                border
                                border-slate-700
                                hover:bg-slate-800
                                transition
                            "
                            title="Open Platform Tour"
                        >
                            <PlayCircle size={24} />
                        </button>
                    )}


                    {/* -------------------------------------------------
                        FULL DESKTOP WIDGET
                    ------------------------------------------------- */}

                    {!isMinimized && (
                        <div
                            className="
                                fixed
                                left-6
                                top-1/2
                                -translate-y-1/2
                                z-40
                                w-80
                            "
                        >
                            <div
                                className="
                                    bg-white
                                    rounded-2xl
                                    border
                                    border-slate-200
                                    shadow-2xl
                                    overflow-hidden
                                "
                            >

                                {/* HEADER */}

                                <div
                                    className="
                                        bg-slate-900
                                        text-white
                                        px-4
                                        py-3
                                        flex
                                        items-center
                                        justify-between
                                    "
                                >

                                    <div>
                                        <h3 className="font-bold text-slate-200">
                                            Platform Tour
                                        </h3>

                                        <p className="text-xs text-slate-300 mt-1">
                                            Explore Orion Analytics
                                        </p>
                                    </div>


                                    {/* MINIMIZE BUTTON */}

                                    <button
                                        onClick={() => setIsMinimized(true)}
                                        className="
                                            ml-3
                                            w-8
                                            h-8
                                            flex
                                            items-center
                                            justify-center
                                            rounded-lg
                                            text-slate-300
                                            hover:bg-slate-700
                                            hover:text-white
                                            transition
                                        "
                                        title="Minimize"
                                    >
                                        <Minus size={18} />
                                    </button>

                                </div>


                                {/* CONTENT */}

                                <div className="p-4">

                                    <p
                                        className="
                                            text-sm
                                            text-slate-600
                                            leading-relaxed
                                            mb-4
                                        "
                                    >
                                        Discover advanced stock screening,
                                        quantitative strategies,
                                        portfolio analytics,
                                        mutual fund research,
                                        options analytics and more.
                                    </p>


                                    {/* PREVIEW */}

                                    <div
                                        className="
                                            relative
                                            cursor-pointer
                                            group
                                        "
                                        onClick={() => setShowVideo(true)}
                                    >
                                        <img
                                            src={preview}
                                            alt="Platform Preview"
                                            className="
                                                w-full
                                                rounded-xl
                                                border
                                                border-slate-200
                                            "
                                        />

                                        <div
                                            className="
                                                absolute
                                                inset-0
                                                bg-black/40
                                                rounded-xl
                                                flex
                                                items-center
                                                justify-center
                                            "
                                        >
                                            <PlayCircle
                                                size={60}
                                                className="text-white"
                                            />
                                        </div>
                                    </div>


                                    {/* WATCH BUTTON */}

                                    <button
                                        onClick={() => setShowVideo(true)}
                                        className="
                                            mt-4
                                            w-full
                                            bg-blue-600
                                            hover:bg-blue-700
                                            text-white
                                            py-3
                                            rounded-xl
                                            font-semibold
                                            transition
                                        "
                                    >
                                        Watch Platform Tour
                                    </button>

                                </div>

                            </div>
                        </div>
                    )}
                </>
            )}


            {/* =====================================================
                VIDEO MODAL
            ===================================================== */}

            {showVideo && (
                <div
                    className="
                        fixed
                        inset-0
                        bg-black/80
                        z-[9999]
                        flex
                        items-center
                        justify-center
                        p-4
                    "
                    onClick={() => setShowVideo(false)}
                >

                    <div
                        className="
                            relative
                            bg-black
                            rounded-2xl
                            overflow-hidden
                            w-full
                            max-w-6xl
                        "
                        onClick={(e) => e.stopPropagation()}
                    >

                        {/* CLOSE */}

                        <button
                            onClick={() => setShowVideo(false)}
                            className="
                                absolute
                                top-3
                                right-3
                                z-20
                                bg-white
                                text-black
                                rounded-full
                                w-9
                                h-9
                                font-bold
                                shadow-lg
                                hover:bg-slate-200
                            "
                        >
                            X
                        </button>


                        {/* VIDEO */}

                        <video
                            controls
                            autoPlay
                            playsInline
                            className="
                                w-full
                                max-h-[90vh]
                            "
                        >
                            <source
                                src={oa}
                                type="video/mp4"
                            />
                        </video>

                    </div>

                </div>
            )}

        </>
    );
}